import { useMutation } from '@apollo/client';

import { apolloClientContracts } from '@/apollo/clients/contracts.apollo';
import { notificationService } from '@/services/notification/notification.client';
import { Unbundle, UnbundleVariables } from
  '@/graphql/contracts/_generated_/unbundle.mutation';
import UnbundleMutation from '@/graphql/contracts/unbundle.mutation.gql';
import GetBCContractLinesQuery from '@/graphql/contracts/getPaginatedFilteredBCContractLines.query.gql';
import GetPaginatedFilteredVariableContractLinesQuery from
  '@/graphql/contracts/getPaginatedFilteredVariableContractLines.query.gql';
import { ALL_ITEMS, } from '@/config/common.config';
import {
  clearLinesSelection,
  triggerLineRefresh,
} from '@/pages/ContractDetails/ContractLines/components/ContractLinesGrid/linesSelectionObservable';
import { triggerVariableRefresh } from
  '@/pages/ContractDetails/ContractLines/components/ContractLinesGrid/variableLinesSelectionObservable';

export default function useUnbundle(contractId: UUID) {
  const [_unbundle, { loading, data }] = useMutation<Unbundle, UnbundleVariables>(
    UnbundleMutation,
    {
      client: apolloClientContracts,
      onError: (err) => notificationService.open({
        title: 'Error',
        text: err.message,
        type: 'danger'
      }),
      onCompleted: (response) => {
        triggerLineRefresh();
        triggerVariableRefresh();
        if (response.unbundle) {
          if ('errorMessage' in response.unbundle) {
            notificationService.open({
              title: 'Error',
              text: response.unbundle.errorMessage,
              type: 'danger'
            });
          } else if ('errors' in response.unbundle) {
            notificationService.open({
              title: 'Warning',
              text: response.unbundle.errors[0]?.message || 'Validation errors occurred',
              type: 'warning'
            });
          } else if ('succeeded' in response.unbundle && response.unbundle.succeeded) {
            clearLinesSelection();
            notificationService.open({
              title: 'Success',
              text: 'Unbundled successfully',
              type: 'success'
            });
          }
        }
      },
      awaitRefetchQueries: true,
      refetchQueries: [
        {
          query: GetBCContractLinesQuery,
          variables: {
            filter: {
              contractId: { eq: contractId }
            },
            first: ALL_ITEMS
          }
        },
        {
          query: GetPaginatedFilteredVariableContractLinesQuery,
          variables: {
            filter: { headerPrimaryKey: { eq: contractId } },
            first: ALL_ITEMS,
          }
        }
      ]
    }
  );

  const unbundle = async (input: UnbundleVariables['input']) => {
    await _unbundle({
      variables: { input }
    });
  };

  return {
    unbundle,
    unbundleResult: data?.unbundle,
    isLoadingUnbundle: loading,
  } as const;
}
